import { useMemo, useState } from 'react';

/** Return a sorted copy of `rows` by a single value accessor. Numbers compare numerically;
 *  everything else as a natural-order string. Nulls sort last regardless of direction. */
export function sortRowsBy<T>(
  rows: T[],
  getVal: (row: T) => string | number | null | undefined,
  dir: 'asc' | 'desc',
): T[] {
  return [...rows].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  });
}

/**
 * Client-side column sort for tables whose full row set is already loaded (not server-paginated).
 * Pass a map of column key → value accessor (define it at module scope so it stays stable).
 * Returns the sorted rows plus the `sortBy` / `sortDir` / `onSort` props to spread onto a
 * `DataTable` or `SortableTh` — same standard arrow UI as the server-side sorted tables.
 *
 * Numbers compare numerically; everything else compares as a natural-order string. Nulls sort last.
 */
export function useClientSort<T>(
  rows: T[],
  sortValues: Record<string, (row: T) => string | number | null | undefined>,
  initial?: { sortBy?: string; sortDir?: 'asc' | 'desc' },
) {
  const [sortBy, setSortBy] = useState<string | null>(initial?.sortBy ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initial?.sortDir ?? 'asc');

  const sorted = useMemo(() => {
    const getVal = sortBy ? sortValues[sortBy] : undefined;
    if (!getVal) return rows;
    return sortRowsBy(rows, getVal, sortDir);
  }, [rows, sortBy, sortDir, sortValues]);

  const onSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
  };

  return { sorted, sortBy, sortDir, onSort };
}

export default useClientSort;
